/**
 * CustomDomainSection Component
 *
 * Main component for custom domain management in project settings.
 * Displays different UI based on domain state:
 * - No domain: "Add Domain" button
 * - Pending DNS: DNS instructions + Verify button
 * - In Progress: Progress indicator
 * - Active: Success state with domain URL
 * - Failed: Error with retry/remove options
 *
 * Pattern reference: src/modules/analytics-settings/components/ConsentManagementSection.tsx
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
  Link,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SDialog } from '@modules/ui-components';

import { useCustomDomain } from '../hooks/useCustomDomain';
import { DomainSetupDialog } from './DomainSetupDialog';
import { DomainSetupForm } from './DomainSetupForm';
import { DomainStatusBadge, getStatusDescription, isErrorStatus } from './DomainStatusBadge';
import { DNSInstructions, extractDisplayHost } from './DNSInstructions';
import type { CnameRecord, CustomDomainSurface, DnsRecords } from '../types/customDomain.types';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';
import { ArticlePathModePanel, ArticlePathSection } from '@modules/custom-domains/components/ArticlePathModePanel';
import { PATH_MODE_COPY } from '@modules/custom-domains/components/articlePathModeCopy';

/**
 * Component props
 */
export interface CustomDomainSectionProps {
  /** Project ID */
  projectId: string;
  /**
   * Serving surface (v2.20.0): 'web' funnel domain (default) or 'article'
   * domain. Same setup wizard; an article domain serves published articles
   * at its root path via the CDN layer.
   */
  surface?: CustomDomainSurface;
  /**
   * Render the setup form WITHOUT the three workflow guide anchors (same effect as the
   * article-surface guard). The section itself never renders them (D-23, plan
   * 2026-10-07-sidebar-subscription-menu): their only copies live inside the setup form
   * (`DomainSetupForm`, dialog or inline). Used by DomainConnectDrawer so the Drawer
   * carries zero workflow anchors (AC-018, DC-19).
   */
  suppressGuideAnchors?: boolean;
  /** data-testid for the pending_dns "Verify Now" button (the Drawer passes its own id, AC-020). */
  verifyButtonTestId?: string;
  /**
   * How the empty state offers "add a domain" (follow-up F1):
   *   'dialog' (default) — an "Add Custom Domain" button opening `DomainSetupDialog`
   *     (Integrations / article surfaces: the section is one of many on a long page).
   *   'inline' — the setup form renders directly in the empty state, no button and no
   *     modal, so a host that is ALREADY a container (the launch-activation domain
   *     Drawer) stays ONE surface instead of drawer → button → dialog.
   * After a successful setup `setupDomain` refetches, the section leaves the empty
   * state, and the inline form unmounts into the normal pending-DNS view.
   */
  addMode?: 'dialog' | 'inline';
  /**
   * Reports which branch the section renders once its status is known (follow-up F1, fix 1):
   * `'empty'` = no domain yet (the Add button / inline setup form), `'has-domain'` = a domain
   * exists (pending DNS, in progress, active or failed). A host chrome that carries its own
   * action — `DomainConnectDrawer`'s footer — uses it to keep exactly ONE primary action per
   * state: while the inline form owns the primary "Continue", the footer must not offer a
   * second identically-labelled button. Never called while `loading`.
   */
  onStateChange?: (state: 'empty' | 'has-domain') => void;
}

/**
 * Custom Domain Section Component
 */
export const CustomDomainSection: React.FC<CustomDomainSectionProps> = (props) => (
  <Box data-testid={CUSTOM_DOMAIN_TEST_IDS.slot(props.surface ?? 'web')}>
    <CustomDomainSectionBody {...props} />
  </Box>
);

const CustomDomainSectionBody: React.FC<CustomDomainSectionProps> = ({
  projectId,
  surface = 'web',
  suppressGuideAnchors = false,
  verifyButtonTestId,
  addMode = 'dialog',
  onStateChange,
}) => {
  const theme = useTheme();
  const isArticleSurface = surface === 'article';
  // Workflow guide anchors render only inside the setup form (D-23) — never on the section.
  const renderAnchors = !isArticleSurface && !suppressGuideAnchors;
  // The funnel-domain empty state with the Add button is centred like the Analytics empty state (D-15).
  const centredEmptyState = !isArticleSurface && addMode === 'dialog';

  // State
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  // Hook
  const {
    domain,
    loading,
    error,
    verifying,
    removing,
    setting,
    setupDomain,
    verifyDomain,
    removeDomain,
    refetch,
  } = useCustomDomain(projectId, surface);

  // Report the rendered branch to a host chrome (see `onStateChange`). Keyed on the boolean,
  // not the domain object, so a status poll that returns an equivalent record does not re-fire.
  const hasDomain = Boolean(domain);
  React.useEffect(() => {
    if (loading) return;
    onStateChange?.(hasDomain ? 'has-domain' : 'empty');
  }, [loading, hasDomain, onStateChange]);

  /**
   * Handle setup dialog open
   */
  const handleOpenSetup = useCallback(() => {
    setSetupDialogOpen(true);
  }, []);

  /**
   * Handle setup dialog close
   */
  const handleCloseSetup = useCallback(() => {
    setSetupDialogOpen(false);
  }, []);

  /**
   * Handle remove dialog open
   */
  const handleOpenRemove = useCallback(() => {
    setRemoveDialogOpen(true);
  }, []);

  /**
   * Handle remove dialog close
   */
  const handleCloseRemove = useCallback(() => {
    setRemoveDialogOpen(false);
  }, []);

  /**
   * Handle domain removal
   */
  const handleRemove = useCallback(async () => {
    try {
      await removeDomain();
      setRemoveDialogOpen(false);
    } catch {
      // Error is handled by hook
    }
  }, [removeDomain]);

  /**
   * Handle verify click
   */
  const handleVerify = useCallback(async () => {
    try {
      await verifyDomain();
    } catch {
      // Error is handled by hook
    }
  }, [verifyDomain]);

  // Loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" height={80} sx={{ mt: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state (API error)
  if (error && !domain) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  // No domain configured
  if (!domain) {
    return (
      <Box
        data-testid={isArticleSurface ? CUSTOM_DOMAIN_TEST_IDS.articleSection : CUSTOM_DOMAIN_TEST_IDS.section}
        sx={centredEmptyState ? { textAlign: 'center', py: 4, color: 'text.secondary' } : undefined}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom={centredEmptyState}
          paragraph={!centredEmptyState}
        >
          {isArticleSurface
            ? 'Connect a subdomain for published articles — each article is served at its root path (e.g. article.yourdomain.com/my-article) straight from the CDN.'
            : 'Connect your own subdomain to access funnels without the /w/ prefix.'}
        </Typography>

        {addMode === 'inline' ? (
          // The setup form IS the empty state — one container, no modal hop. It carries the
          // workflow guide anchors itself unless the host suppresses them (the Drawer does).
          <DomainSetupForm
            onSetup={setupDomain}
            onDone={refetch}
            suppressGuideAnchors={!renderAnchors}
          />
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenSetup}
              disabled={setting}
              size={centredEmptyState ? 'small' : undefined}
              sx={centredEmptyState ? { mt: 1 } : undefined}
              data-testid={isArticleSurface ? CUSTOM_DOMAIN_TEST_IDS.articleAddBtn : CUSTOM_DOMAIN_TEST_IDS.addBtn}
            >
              {isArticleSurface ? 'Add Article Domain' : 'Add Custom Domain'}
            </Button>

            <DomainSetupDialog
              open={setupDialogOpen}
              onClose={handleCloseSetup}
              onSetup={setupDomain}
              suppressGuideAnchors={!renderAnchors}
            />
          </>
        )}
      </Box>
    );
  }

  // Build CNAME record if available
  const cnameRecord: CnameRecord | null = domain.dnsAuthorization
    ? {
        name: domain.dnsAuthorization.cnameRecord.name,
        type: 'CNAME',
        data: domain.dnsAuthorization.cnameRecord.data,
      }
    : null;

  // Status-specific content
  const showDnsInstructions = ['pending_dns', 'verifying_dns'].includes(domain.status);
  const showProgressIndicator = ['dns_verified', 'provisioning_cert', 'configuring_lb'].includes(domain.status);
  const isActive = domain.status === 'active';
  const isFailed = isErrorStatus(domain.status);

  // Domain actions (Verify Now / Remove Domain / Retry).
  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {/* Verify Button (for pending_dns) */}
      {domain.status === 'pending_dns' && (
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={verifying}
          startIcon={verifying ? <CircularProgress size={16} /> : <RefreshIcon />}
          data-testid={verifyButtonTestId ?? CUSTOM_DOMAIN_TEST_IDS.verifyBtn}
        >
          {verifying ? 'Verifying...' : 'Verify Now'}
        </Button>
      )}

      {/* Remove Button */}
      <Button
        variant="outlined"
        color="error"
        onClick={handleOpenRemove}
        disabled={removing}
        startIcon={<DeleteOutlineIcon />}
        data-testid={CUSTOM_DOMAIN_TEST_IDS.removeBtn}
      >
        Remove Domain
      </Button>

      {/* Retry Button (for failed) */}
      {isFailed && (
        <Button
          variant="outlined"
          onClick={handleVerify}
          disabled={verifying}
          startIcon={<RefreshIcon />}
          data-testid={CUSTOM_DOMAIN_TEST_IDS.retryBtn}
        >
          Retry
        </Button>
      )}
    </Box>
  );

  return (
    <Box data-testid={isArticleSurface ? CUSTOM_DOMAIN_TEST_IDS.articleSection : CUSTOM_DOMAIN_TEST_IDS.section}>
      {/* Domain Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="body1"
          fontWeight={500}
          sx={{ fontFamily: 'monospace' }}
        >
          {domain.domain}
        </Typography>
        <DomainStatusBadge status={domain.status} />
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Last error from domain */}
      {domain.lastError && isFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            {domain.lastError.message}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(domain.lastError.timestamp).toLocaleString()}
          </Typography>
        </Alert>
      )}

      {/* DNS Instructions (pending_dns, verifying_dns) */}
      {showDnsInstructions && (domain.dnsRecords || cnameRecord) && (
        <Box sx={{ mb: 2 }}>
          {/* Use new dnsRecords format if available, fallback to legacy cnameRecord */}
          {domain.dnsRecords ? (
            <DNSInstructions dnsRecords={domain.dnsRecords} />
          ) : cnameRecord ? (
            <DNSInstructions
              cnameRecord={cnameRecord}
              displayHost={extractDisplayHost(cnameRecord.name, domain.domain)}
            />
          ) : null}
        </Box>
      )}

      {/* Progress Indicator (dns_verified, provisioning_cert, configuring_lb) */}
      {showProgressIndicator && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 2 }}
        >
          {getStatusDescription(domain.status)}
        </Alert>
      )}

      {/* Active State */}
      {isActive && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            {isArticleSurface
              ? 'Your published articles are now accessible at:'
              : 'Your funnels are now accessible at:'}
          </Typography>
          <Link
            href={`https://${domain.domain}/`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontFamily: 'monospace',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            https://{domain.domain}/&#123;alias&#125;
            <OpenInNewIcon fontSize="small" />
          </Link>
        </Alert>
      )}

      {/* Action Buttons — on an active article domain the path-mode panel places them under the Home page select (D-2) */}
      {isArticleSurface && isActive ? (
        <ArticlePathModePanel projectId={projectId} articleHost={domain.domain} domainActions={actionButtons} />
      ) : (
        actionButtons
      )}

      {/* Path on your main site: only for an ACTIVE article domain (AC-035); otherwise a hint, never a disabled form */}
      {isArticleSurface && !isActive && (
        <ArticlePathSection>
          <Typography variant="body2" color="text.secondary">
            {PATH_MODE_COPY.needsDomain}
          </Typography>
        </ArticlePathSection>
      )}

      {/* Remove Confirmation Dialog */}
      <SDialog
        open={removeDialogOpen}
        onClose={handleCloseRemove}
        title={isArticleSurface ? 'Remove Article Domain?' : 'Remove Custom Domain?'}
        maxWidth="xs"
        // Destructive confirmation — footer Cancel is the explicit dismiss;
        // no header X to avoid ambiguity between the two surfaces.
        showCloseButton={false}
        primaryAction={{
          label: removing ? 'Removing...' : 'Remove',
          onClick: handleRemove,
          disabled: removing,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: handleCloseRemove,
          disabled: removing,
        }}
      >
        <Typography variant="body2" paragraph>
          Are you sure you want to remove <strong>{domain.domain}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This will disconnect the domain and delete the SSL certificate.
          {isArticleSurface
            ? ' Articles will only be accessible via the default URL.'
            : ' Funnels will only be accessible via the default URL.'}
        </Typography>
      </SDialog>
    </Box>
  );
};

export default CustomDomainSection;
